import { useEffect, useState } from 'react';
import {
  auditFilename, buildAudit, buildCSV, buildStatement, copyText, csvFilename,
  shareFile, statementFilename, statementPdf
} from '../lib/exports';
import { track } from '../lib/analytics';
import { Icon } from '../components/Icons';
import { Note, Sheet, useToast } from '../components/ui';
import type { TripData } from '../types';

type Kind = 'menu' | 'audit' | 'csv' | 'statement';

export default function ExportSheet({ data, onClose }: { data: TripData; onClose: () => void }) {
  const [kind, setKind] = useState<Kind>('menu');
  const [pdf, setPdf] = useState<File | null>(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const toast = useToast();

  const text = kind === 'audit' ? buildAudit(data) : kind === 'csv' ? buildCSV(data) : '';

  // Build the PDF as soon as the statement opens, so Share hands over a ready
  // file within the tap — iOS refuses a share that arrives seconds after it.
  useEffect(() => {
    if (kind !== 'statement') return;
    let alive = true;
    setPdf(null);
    setPdfFailed(false);
    statementPdf(data)
      .then((blob) => {
        if (alive) setPdf(new File([blob], statementFilename(data.group.name), { type: 'application/pdf' }));
      })
      .catch(() => { if (alive) setPdfFailed(true); });
    return () => { alive = false; };
  }, [kind, data]);

  async function copy(label: string) {
    track('share', { content_type: kind, method: 'copy' });
    toast(await copyText(text) ? `${label} copied` : 'Select the text above to copy it');
  }

  // Share the file straight from the tap; logging it first costs nothing.
  function save(file: File) {
    track('share', { content_type: file.name.split('.').pop() ?? 'file', method: 'file' });
    void shareFile(file);
  }

  return (
    <Sheet
      title={kind === 'menu' ? 'Export' : kind === 'audit' ? 'Check with AI'
        : kind === 'csv' ? 'Spreadsheet' : 'Statement'}
      onClose={onClose}
      footer={kind === 'menu' ? undefined : (
        <div className="xacts">
          <button className="btn ghost" onClick={() => setKind('menu')}>Back</button>
          {kind === 'statement' ? (
            <button className="btn" disabled={!pdf} onClick={() => { if (pdf) save(pdf); }}>
              {pdf ? 'Share PDF' : pdfFailed ? 'PDF not ready' : 'Preparing PDF…'}
            </button>
          ) : (
            <button className="btn" onClick={() => void copy(kind === 'audit' ? 'Prompt' : 'CSV')}>
              Copy
            </button>
          )}
        </div>
      )}
    >
      {kind === 'menu' && (
        <>
          <div className="list" style={{ marginTop: 4 }}>
            <button className="exp-opt" onClick={() => setKind('audit')}>
              <span className="glyph tint"><Icon name="ai" /></span>
              <span className="main">
                <span className="t">Check the math with AI</span>
                <span className="s">
                  The whole ledger plus a prompt, ready to paste into ChatGPT, Claude or Gemini.
                  It recomputes every split from scratch and reports anything that disagrees.
                </span>
              </span>
            </button>

            <button className="exp-opt" onClick={() => setKind('csv')}>
              <span className="glyph"><Icon name="sheet" /></span>
              <span className="main">
                <span className="t">Spreadsheet (CSV)</span>
                <span className="s">
                  One row per expense with each family's share as its own column, then balances
                  and the settle-up plan. Opens in Excel or Google Sheets.
                </span>
              </span>
            </button>

            <button className="exp-opt" onClick={() => setKind('statement')}>
              <span className="glyph"><Icon name="share" /></span>
              <span className="main">
                <span className="t">Statement</span>
                <span className="s">
                  A clean one-page summary of who paid what and who owes whom, as a PDF
                  you can share or keep.
                </span>
              </span>
            </button>
          </div>

          <div className="xacts" style={{ marginTop: 14 }}>
            <button
              className="btn ghost"
              onClick={() => save(new File([buildAudit(data)], auditFilename(data.group.name),
                { type: 'text/plain;charset=utf-8' }))}
            >
              Save audit
            </button>
            <button
              className="btn ghost"
              onClick={() => save(new File([buildCSV(data)], csvFilename(data.group.name),
                { type: 'text/csv;charset=utf-8' }))}
            >
              Save CSV
            </button>
          </div>

          <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, margin: '14px 2px 0' }}>
            On a phone, Save opens the share sheet so a file can go straight into WhatsApp or
            Mail. On a desktop it downloads.
          </p>
        </>
      )}

      {(kind === 'audit' || kind === 'csv') && <div className="xbox">{text}</div>}

      {kind === 'statement' && (
        <>
          {pdfFailed && (
            <div style={{ marginTop: 4 }}>
              <Note>
                Couldn't make the PDF — the app may need a connection the first time. Go back
                and open the statement again to retry.
              </Note>
            </div>
          )}
          <div className="xdoc" dangerouslySetInnerHTML={{ __html: buildStatement(data) }} />
        </>
      )}
    </Sheet>
  );
}
